import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Button,
    CircularProgress,
    Alert
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AuthService } from '@df/api';
import { getSafeApiErrorMessage, isNetworkError } from '../../utils/errorUtils';
import {
    clearScopedInvitationContinuation,
    getScopedInvitationToken,
    hasScopedInvitationContinuation,
} from '../../utils/scopedInvitationSession';
import ContinuationShell from '../../components/ContinuationShell';

const EmailVerification = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [loading, setLoading] = useState(true);
    const [status, setStatus] = useState(null); // 'success' | 'error' | 'loading'
    const [message, setMessage] = useState('');
    const [error, setError] = useState(null);
    const [successDestination, setSuccessDestination] = useState('/portal');

    // Get uid and token from URL parameters (Djoser format)
    const uid = searchParams.get('uid');
    const token = searchParams.get('token');

    useEffect(() => {
        if (!uid || !token) {
            setStatus('error');
            setMessage('Invalid or missing verification parameters. Please request a new verification email.');
            setLoading(false);
            return;
        }

        // Automatically verify email on component mount
        verifyEmail();
    }, [uid, token]);

    const verifyEmail = async () => {
        if (!uid || !token) return;

        setLoading(true);
        setStatus('loading');
        
        try {
            const response = await AuthService.verifyEmail(uid, token);
            const hasScopedContinuation = hasScopedInvitationContinuation();
            const scopedInvitationToken = getScopedInvitationToken();

            if (hasScopedContinuation && scopedInvitationToken) {
                setSuccessDestination('/scoped-invitation');
                setStatus('success');
                setMessage('Email verified successfully. Return to your invitation to accept it explicitly.');
                setTimeout(() => {
                    navigate('/scoped-invitation');
                }, 2000);
                return;
            }

            if (hasScopedContinuation && !scopedInvitationToken) {
                clearScopedInvitationContinuation();
                setSuccessDestination(null);
                setStatus('success');
                setMessage('Email verified successfully. Reopen the original invitation email link to continue.');
                return;
            }
            setSuccessDestination('/portal');
            setStatus('success');
            setMessage('Email verified successfully! You can now access your account.');

            // Redirect to portal after a short delay
            setTimeout(() => {
                navigate('/portal');
            }, 2000);

        } catch (error) {
            console.error('Email verification error:', error);
            setStatus('error');
            if (isNetworkError(error)) {
                setMessage('Server unreachable. Is the backend running?');
            } else {
                setMessage(getSafeApiErrorMessage(error, 'Email verification failed. Please try again.'));
            }
            setError(error);
        } finally {
            setLoading(false);
        }
    };

    const handleRetry = () => {
        if (token) {
            verifyEmail();
        }
    };

    const handleGoToLogin = () => {
        navigate('/login');
    };

    const handleRequestNewVerification = () => {
        navigate('/resend-verification');
    };

    return (
        <ContinuationShell cardSx={{ textAlign: 'center' }}>
                    {/* Header */}
                    <Box mb={4}>
                        <Typography variant="h4" component="h1" gutterBottom sx={{ color: '#2c5aa0', fontWeight: 'bold' }}>
                            Email Verification
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                            Verifying your email address...
                        </Typography>
                    </Box>

                    {/* Status Display */}
                    <Box mb={4}>
                        {status === 'loading' && (
                            <Box>
                                <CircularProgress size={60} sx={{ color: '#2D5EFF', mb: 2 }} />
                                <Typography variant="body1" color="text.secondary">
                                    Please wait while we verify your email...
                                </Typography>
                            </Box>
                        )}

                        {status === 'success' && (
                            <Box>
                                <CheckCircleIcon sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
                                <Typography variant="h6" color="success.main" gutterBottom>
                                    Email Verified Successfully!
                                </Typography>
                                <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                                    {message}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {successDestination === '/scoped-invitation'
                                        ? 'Redirecting back to your invitation...'
                                        : successDestination
                                            ? 'Redirecting to your dashboard...'
                                            : 'Reopen the original invitation email to continue.'}
                                </Typography>
                                {successDestination ? (
                                    <Button
                                        variant="contained"
                                        onClick={() => navigate(successDestination)}
                                        sx={{ mt: 2 }}
                                    >
                                        {successDestination === '/scoped-invitation'
                                            ? 'Return to Invitation'
                                            : 'Continue to Portal'}
                                    </Button>
                                ) : (
                                    <Button
                                        variant="contained"
                                        onClick={handleGoToLogin}
                                        sx={{ mt: 2 }}
                                    >
                                        Back to Login
                                    </Button>
                                )}
                            </Box>
                        )}

                        {status === 'error' && (
                            <Box>
                                <ErrorIcon sx={{ fontSize: 80, color: 'error.main', mb: 2 }} />
                                <Typography variant="h6" color="error.main" gutterBottom>
                                    Verification Failed
                                </Typography>
                                <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                                    {message}
                                </Typography>
                            </Box>
                        )}
                    </Box>

                    {/* Action Buttons */}
                    {status === 'error' && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Button
                                variant="contained"
                                onClick={handleRetry}
                                disabled={!token}
                                sx={{
                                    py: 1.5,
                                    backgroundColor: '#2D5EFF',
                                    '&:hover': {
                                        backgroundColor: '#1e4bd8'
                                    }
                                }}
                            >
                                Try Again
                            </Button>
                            
                            <Button
                                variant="outlined"
                                onClick={handleRequestNewVerification}
                                sx={{
                                    py: 1.5,
                                    borderColor: '#2D5EFF',
                                    color: '#2D5EFF',
                                    '&:hover': {
                                        borderColor: '#1e4bd8',
                                        backgroundColor: 'rgba(45, 94, 255, 0.04)'
                                    }
                                }}
                            >
                                Request New Verification Email
                            </Button>
                            
                            <Button
                                variant="text"
                                onClick={handleGoToLogin}
                                sx={{
                                    py: 1.5,
                                    color: '#666'
                                }}
                            >
                                Back to Login
                            </Button>
                        </Box>
                    )}

                    {/* Error Details (for debugging) */}
                    {error && process.env.NODE_ENV === 'development' && (
                        <Alert severity="error" sx={{ mt: 3, textAlign: 'left' }}>
                            <Typography variant="body2">
                                <strong>Debug Info:</strong><br />
                                Status: {error.status || error.response?.status || 'Unknown'}<br />
                                Message: {message}<br />
                                Token: {token ? 'Present' : 'Missing'}
                            </Typography>
                        </Alert>
                    )}
        </ContinuationShell>
    );
};

export default EmailVerification;
